// [src/pages/Product/Product.jsx]

import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL } from "config/apiConfig";
import { getTokenStatus } from "utils/tokenManager";
import { useBranch } from "contexts/BranchContext";

// Styles & Assets
import 'styles/Font.css';
import boxUncheckedIcon from "assets/images/icon/box-unchecked-icon.svg";
import boxCheckedIcon from "assets/images/icon/box-checked-icon.svg";

// Components
import TopNavigator from "components/layout/TopNavigator/TopNavigator";
import Footer from "components/layout/Footer/Footer";
import ConfirmModal from "components/ui/Modals/ConfirmModal/ConfirmModal";

// =======================================================================
// API 호출 함수
// =======================================================================
const fetchProductDataApi = async (brKey, prodKind, prodGubun) => {
    // prodKind가 있을 때만 쿼리 스트링에 포함 (&prodKind=값), 없으면 빈 문자열
    const kindQuery = prodKind ? `&prodKind=${prodKind}` : '';

    // 최종 URL 조립
    const API_URL = `${API_BASE_URL}/prod?brKey=${brKey}${kindQuery}&prodGubun=${prodGubun}&prodKiosk=Y`;
    const { token } = getTokenStatus();
    try {
        const response = await fetch(API_URL, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            throw new Error(`API 호출 실패: HTTP 상태 ${response.status}`);
        }

        const data = await response.json();
        const res = data.data || data;

        const productList = res.list || [];
        const etcList = res.etc || [];

        const plans = [];
        const additionalProducts = [];
        const pricePerItem = 1000;

        // 메인 상품 파싱
        if (Array.isArray(productList)) {
            productList.forEach((item) => {
                const price = parseInt(item.pdPrice || 0, 10);
                const isMonthlyPlan = item.prodGubun === 'Y';

                // 개월 수 파싱 및 저장
                let months = 1;
                if (!isMonthlyPlan && item.pdName) {
                    const match = item.pdName.match(/(\d+)\s*개월/);
                    if (match && match[1]) {
                        months = parseInt(match[1], 10);
                    }
                }
                months = Math.max(1, months);

                const plan = {
                    id: parseInt(item.pdKey, 10),
                    title: item.pdName || '상품명 없음',
                    originalPrice: price,
                    discountRate: 0,
                    discountPrice: price,
                    isMonthly: isMonthlyPlan,
                    prodGubun: item.prodGubun,
                    months: months, // 개월 수 저장
                };

                if (!isMonthlyPlan) {
                    plan.monthlyEquivalentPrice = Math.floor(price / months);
                }
                plans.push(plan);
            });
        }

        // 추가 상품 파싱
        if (Array.isArray(etcList)) {
            etcList.forEach((item) => {
                const price = parseInt(item.pdPrice || 0, 10);
                const isMonthly = item.prodGubun === 'Y';

                additionalProducts.push({
                    id: parseInt(item.pdKey, 10),
                    title: item.pdName || '추가 상품',
                    price: price, // [주의] 여기는 월 단가(혹은 기본 단가)
                    isMonthly: isMonthly,
                });
            });
        }

        return { plans, additionalProducts, pricePerItem };

    } catch (e) {
        console.error(`지점 상품 목록 API 호출 에러 (${brKey}, ${prodGubun}):`, e);
        throw new Error('상품 목록을 불러오는 데 실패했습니다.');
    }
};

// =======================================================================
// 유틸리티 컴포넌트
// =======================================================================
const formatPrice = (price) => price.toLocaleString('ko-KR');

const renderBadge = (badge) => {
    if (!badge) return null;
    const isBest = badge === 'BEST';
    return (
        <div style={{ ...staticStyles.badgeContainerBase, background: isBest ? '#8052FF' : '#F54D3F' }}>
            <span style={staticStyles.badgeText}>{isBest ? 'BEST' : '추천'}</span>
        </div>
    );
};

const PriceDisplay = ({ plan }) => {
    const { discountPrice, isMonthly, monthlyEquivalentPrice } = plan;

    if (isMonthly) {
        return (
            <div style={staticStyles.priceAreaMonthly}>
                <div><span style={staticStyles.originalPriceMonthly}>월 {formatPrice(discountPrice)}원</span></div>
            </div>
        );
    }

    if (!isMonthly && monthlyEquivalentPrice) {
        return (
            <div style={staticStyles.priceAreaPass}>
                <div style={staticStyles.totalPassPriceBox}>
                    <span style={staticStyles.totalPassPrice}>{formatPrice(discountPrice)}원</span>
                </div>
                <div>
                    <span style={staticStyles.monthlyEquivalentPrice}>월 {formatPrice(monthlyEquivalentPrice)}원</span>
                </div>
            </div>
        );
    }
    return null;
};

const SubscriptionCard = ({ plan, isSelected, onSelect }) => {
    const cardBaseStyle = { ...staticStyles.subscriptionCardOuterBase, marginBottom: '10px', cursor: 'pointer' };
    const outerStyle = isSelected ? staticStyles.gradientBorderWrapper : staticStyles.subscriptionCardBaseCompensated;

    return (
        <div style={{ ...cardBaseStyle, ...outerStyle }} onClick={() => onSelect(plan.id)}>
            <div style={staticStyles.innerCardContent}>
                <div style={staticStyles.cardTitleBox}>
                    <span style={staticStyles.cardTitle}>{plan.title}</span>
                    {renderBadge(plan.badge)}
                </div>
                <PriceDisplay plan={plan} />
            </div>
        </div>
    );
};

// 추가 상품 아이템 (개월 수 반영)
const ExtraProductItem = ({ product, isChecked, onToggle, months }) => {
    // 월 가격 * 개월 수 = 최종 표시 가격
    const totalPrice = product.price * months;
    const formattedPrice = formatPrice(totalPrice);

    const id = `extra-product-${product.id}`;
    const checkboxStyles = isChecked ? staticStyles.extraProductCircleCheckbox.checked : staticStyles.extraProductCircleCheckbox.unchecked;
    const itemStyles = isChecked ? staticStyles.extraProductItem.checked : staticStyles.extraProductItem.unchecked;

    return (
        <label htmlFor={id} style={{ ...staticStyles.extraProductItemBase, ...itemStyles }}>
            <input id={id} type="checkbox" checked={isChecked} onChange={() => onToggle(product.id)}
                style={staticStyles.hiddenInput} />
            <div style={staticStyles.extraProductInnerContent}>
                <div style={{ ...staticStyles.extraProductCircleCheckbox.base, ...checkboxStyles }}>
                    {isChecked && (<div style={staticStyles.extraProductInnerDot} />)}
                </div>
                <div style={staticStyles.extraProductInfoBox}>
                    <span style={staticStyles.extraProductTitle}>{product.title}</span>
                    <span style={staticStyles.extraProductPrice}>
                        {formattedPrice}원
                        {/* 1개월 이상일 경우 개월 수 표시 (선택사항) */}
                        {months > 1 &&
                            <span style={{ fontSize: '12px', fontWeight: '400', marginLeft: '4px' }}>({months}개월)</span>}
                    </span>
                </div>
            </div>
        </label>
    );
};

const SubscriptionList = ({ plans, selectedId, onSelect }) => (
    <div style={staticStyles.subscriptionList}>
        {plans.map((plan) => (
            <SubscriptionCard key={plan.id} plan={plan} isSelected={plan.id === selectedId} onSelect={onSelect} />
        ))}
    </div>
);

// =======================================================================
// 메인 컴포넌트
// =======================================================================
function Product() {
    const { brKey } = useParams();
    const navigate = useNavigate();

    // 탭 상태 관리 변경 (기존 activeProdGubun -> activeTab)
    // 가능한 값: 'SUBSCRIPTION', 'PASS', 'SLEEK'
    const [activeTab, setActiveTab] = useState('SUBSCRIPTION');
    const [plans, setPlans] = useState([]);
    const [additionalProducts, setAdditionalProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [selectedAdditionalItems, setSelectedAdditionalItems] = useState({});
    const [startDayType] = useState(''); // 상시 결제 (원본 유지용)
    const [isAgreed, setIsAgreed] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { currentBranch } = useBranch();
    const brName = `${currentBranch?.brName || ""} 상품 구매`;
    // 결제 진행 중 상태
    const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);

    // ▼▼▼ [AttendanceHistory.jsx와 동일한 로직 적용] ▼▼▼
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(60); // 안전한 초기값

    useEffect(() => {
        const updateHeight = () => {
            if (headerRef.current) {
                // border 포함 높이 측정
                setHeaderHeight(headerRef.current.offsetHeight);
            }
        };

        // 초기 측정
        updateHeight();

        // 미세한 로딩 지연(폰트 등) 대비 안전장치
        setTimeout(updateHeight, 100);
        setTimeout(updateHeight, 300);

        // 요소 크기 변화 실시간 감지 (ResizeObserver) -> 가장 핵심!
        const observer = new ResizeObserver(() => {
            updateHeight();
        });

        if (headerRef.current) {
            observer.observe(headerRef.current);
        }

        // 브라우저 창 리사이즈 감지
        window.addEventListener('resize', updateHeight);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateHeight);
        };
    }, []);
    // ▲▲▲ -------------------------------------------- ▲▲▲

    // [중요] 탭에 따른 API 파라미터 결정 로직
    const getApiParams = (tab) => {
        switch (tab) {
            case 'SUBSCRIPTION': // 구독권
                // kind를 null로 설정 -> API URL에서 빠짐
                return { kind: null, gubun: 'Y' };
            case 'PASS':         // 이용권
                return { kind: 'A', gubun: 'N' };
            case 'SLEEK':        // 슬릭
                return { kind: 'L', gubun: 'N' };
            default:
                return { kind: null, gubun: 'Y' };
        }
    };
    // 데이터 로드
    useEffect(() => {
        if (!brKey) {
            setIsLoading(false);
            setError("Error: 지점 키(brKey)가 없습니다");
            return;
        }

        const loadProductData = async () => {
            setPlans([]);
            setSelectedId(null);
            setIsLoading(true);
            setError(null);

            // 현재 탭에 맞는 파라미터 가져오기
            const { kind, gubun } = getApiParams(activeTab);

            try {
                // 결정된 kind와 gubun으로 API 호출
                const productData = await fetchProductDataApi(brKey, kind, gubun);
                setPlans(productData.plans);
                setAdditionalProducts(productData.additionalProducts);

                if (productData.plans.length > 0) {
                    setSelectedId(productData.plans[0].id);
                }
                setIsAgreed(false);
            } catch (e) {
                setError(e.message || '상품 목록 로딩에 실패했습니다.');
            } finally {
                setIsLoading(false);
            }
        };
        loadProductData();
    }, [brKey, activeTab]); // 의존성 배열 activeTab으로 변경

    // 결제 정보 계산
    const selectedPlan = useMemo(() => plans.find(p => p.id === selectedId), [plans, selectedId]);

    // 현재 선택된 플랜의 개월 수 (없으면 1개월)
    const currentMonths = useMemo(() => selectedPlan ? selectedPlan.months : 1, [selectedPlan]);

    // 운동 시작일 (startDate) 실제 날짜 계산 로직: 결제일(오늘) + 1일
    const selectedStartDate = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        return d;
    }, []);

    const formattedStartDate = useMemo(() => {
        return `${selectedStartDate.getFullYear()}.${(selectedStartDate.getMonth() + 1).toString().padStart(2, '0')}.${selectedStartDate.getDate().toString().padStart(2, '0')}`;
    }, [selectedStartDate]);

    // 다음 결제일 고정 주기 산출 (3단계 규칙)
    // 1일: 다음 달 1일 / 2~16일: 다음 달 16일 / 16일 초과: 다다음 달 1일
    const nextBillingDate = useMemo(() => {
        if (!selectedPlan) return "날짜 계산 불가";

        const today = new Date();
        const day = today.getDate();
        let nextDate;

        if (day === 1) {
            // 1일 결제 -> 다음 달 1일
            nextDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        } else if (day >= 2 && day <= 16) {
            // 2일~16일 결제 -> 다음 달 16일
            nextDate = new Date(today.getFullYear(), today.getMonth() + 1, 16);
        } else {
            // 16일 초과 결제 -> 다다음 달 1일
            nextDate = new Date(today.getFullYear(), today.getMonth() + 2, 1);
        }

        return `${nextDate.getFullYear()}.${(nextDate.getMonth() + 1).toString().padStart(2, '0')}.${nextDate.getDate().toString().padStart(2, '0')}`;
    }, [selectedPlan]);

    const paymentSummary = useMemo(() => {
        const extras = additionalProducts.filter(p => selectedAdditionalItems[p.id] > 0);
        if (!selectedPlan) return { title: "오류", message: "선택된 상품 없음", totalPrice: 0 };

        let total = selectedPlan.discountPrice;

        // 추가 상품 가격 계산 (월 가격 * 개월 수)
        extras.forEach(extra => {
            const extraPrice = extra.price * currentMonths;
            total += extraPrice * (selectedAdditionalItems[extra.id] || 0);
        });

        return {
            title: "결제 확인",
            message: `${brName}\n총 ${formatPrice(total)}원을(를)\n결제하시겠습니까?`,
            totalPrice: total
        };
    }, [selectedPlan, selectedAdditionalItems, additionalProducts, currentMonths, brName]);

    // 핸들러
    const handleSelect = (id) => setSelectedId(id);
    const handleExtraProductToggle = (id) => {
        setSelectedAdditionalItems(prev => {
            if (prev[id] > 0) {
                const newState = { ...prev };
                delete newState[id];
                return newState;
            } else return { ...prev, [id]: 1 };
        });
    };

    const handleTabClick = (tab) => setActiveTab(tab);

    // 결제 버튼 클릭 핸들러 (주소 확인 로직 제거)
    const handlePaymentClick = () => {
        if (!selectedId) return alert('상품을 선택해주세요.');
        if (activeTab === 'SUBSCRIPTION' && !isAgreed) return alert('정기결제 상품 구매 동의가 필요합니다.');

        // 주소 확인 없이 바로 모달 오픈
        setIsModalOpen(true);
    };

    const handleModalClose = () => setIsModalOpen(false);

    // [결제 요청]
    const handleModalConfirm = async () => {
        const PAYMENT_API_URL = `${API_BASE_URL}/payment`;
        const RETURN_URL = 'https://hypehaus.ibuild.kr/#/payment/success';
        const CANCEL_URL = `https://hypehaus.ibuild.kr/#/payment/cancel`;
        const { token } = getTokenStatus();

        // 중복 결제 방지
        if (isPaymentProcessing) return;
        setIsPaymentProcessing(true);

        if (!selectedPlan) {
            alert("결제할 상품을 찾을 수 없습니다.");
            setIsPaymentProcessing(false); // 처리 완료
            return handleModalClose();
        }

        // 메인 상품 ID와 추가 상품 ID 수집
        const pdKeyList = [selectedPlan.id];
        Object.keys(selectedAdditionalItems).forEach((key) => {
            if (selectedAdditionalItems[key] > 0) {
                pdKeyList.push(parseInt(key, 10));
            }
        });

        // [핵심 변경] payload를 객체({})가 아닌 '이중 배열([])'로 정의합니다.
        // URLSearchParams는 이 구조를 받으면 같은 키('pdKey')를 여러 번 전송할 수 있게 해줍니다.
        const payload = [
            ['brKey', brKey],
            ['amount', String(paymentSummary.totalPrice)],
            ['return_url', RETURN_URL],
            ['cancel_url', CANCEL_URL],
            ['startDate', startDayType] // 운동 시작일 (null, "1", "15")
        ];

        // pdKey 리스트를 순회하며 payload 배열에 하나씩 추가 (문자열 변환)
        pdKeyList.forEach(key => {
            payload.push(['pdKey[]', String(key)]);
        });

        try {
            const response = await fetch(PAYMENT_API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Authorization': `Bearer ${token}`,
                },
                body: new URLSearchParams(payload),
            });

            // 500 에러 디버깅을 위해 텍스트로 먼저 받기
            const responseText = await response.text();

            if (!response.ok) {
                throw new Error(responseText || `Server Error: ${response.status}`);
            }

            // JSON 파싱 시도
            let data = {};
            try {
                data = JSON.parse(responseText);
            } catch (jsonError) {
                console.error("JSON Parse Error:", jsonError);
                throw new Error("서버 응답 형식이 올바르지 않습니다.");
            }

            if (data.mobile_url || data.online_url) {
                const paymentUrl = data.mobile_url || data.online_url;

                if (window.flutter_inappwebview) {
                    window.flutter_inappwebview.callHandler('OpenPaymentWindow', paymentUrl);
                } else {
                    window.location.href = paymentUrl;
                }
                handleModalClose();
            } else {
                alert(data.message || "결제 요청에 실패했습니다.");
                handleModalClose();
            }
        } catch (e) {
            console.error("Payment Error:", e);
            const displayMsg = e.message && e.message.includes('<!DOCTYPE')
                ? '서버 내부 오류(500)가 발생했습니다.'
                : e.message;
            alert(`결제 실패: ${displayMsg}`);
            handleModalClose();
        } finally {
            setIsPaymentProcessing(false); // [필수] 로딩 해제
        }
    };

    const isSubscriptionTab = activeTab === 'SUBSCRIPTION';
    const dynamicMainContentStyle = {
        ...staticStyles.mainContentBase,
        // 측정된 높이만큼 밀어줌
        paddingTop: `${headerHeight}px`,
        paddingBottom: '100px',
        paddingLeft: '24px',
        paddingRight: '24px',
    };

    if (isLoading) return <div style={staticStyles.pageContainer}><TopNavigator title="상품 구매" />
        <div style={staticStyles.loadingWrapper}><p style={staticStyles.loadingText}>로딩 중...</p></div>
    </div>;
    if (error) return <div style={staticStyles.pageContainer}><TopNavigator title="상품 구매" />
        <div style={staticStyles.loadingWrapper}><p style={{ ...staticStyles.loadingText, color: '#FF5252' }}>{error}</p>
            <button onClick={() => navigate(-1)} style={staticStyles.retryButton}>뒤로 가기</button>
        </div>
    </div>;

    return (
        <div style={staticStyles.backgroundWrapper}>
            {/* [필수] ref={headerRef} 연결 */}
            <div style={staticStyles.fixedHeaderWrapper} ref={headerRef}>
                <div className="statusBar" style={staticStyles.statusBar} />
                <TopNavigator title={brName} />
            </div>

            <main style={dynamicMainContentStyle}>
                <div style={staticStyles.purchaseSelectContainer}>
                    {/* [수정] 탭 목록에 SLEEK 추가 및 렌더링 로직 변경 */}
                    {['SUBSCRIPTION', 'PASS', 'SLEEK'].map(tab => {
                        const isActive = activeTab === tab;

                        let label = '';
                        if (tab === 'SUBSCRIPTION') label = '구독권';
                        else if (tab === 'PASS') label = '이용권';
                        else if (tab === 'SLEEK') label = 'SLEEK';

                        return (
                            <button key={tab} onClick={() => handleTabClick(tab)}
                                style={staticStyles.purchaseButtonBase}>
                                <span
                                    style={{ ...staticStyles.purchaseButtonTextBase, ...(isActive ? staticStyles.purchaseButtonTextActive : staticStyles.purchaseButtonTextInactive) }}>
                                    {label}
                                </span>
                                <div
                                    style={{ ...staticStyles.purchaseButtonIndicatorBase, ...(isActive ? staticStyles.purchaseButtonIndicatorActive : staticStyles.purchaseButtonIndicatorInactive) }} />
                            </button>
                        )
                    })}
                </div>

                <div style={{ width: '100%' }}>
                    <SubscriptionList plans={plans} selectedId={selectedId} onSelect={handleSelect} />
                </div>

                {isSubscriptionTab && (
                    <>
                        <div style={staticStyles.startDateContainer}>
                            <span style={staticStyles.nextBillingText}>운동 시작일: {formattedStartDate}</span>
                        </div>
                        <span style={staticStyles.nextBillingText}>다음 결제일: {nextBillingDate}</span>
                        <label style={staticStyles.agreementContainer} htmlFor="payment-agree">
                            <input type="checkbox" id="payment-agree" checked={isAgreed}
                                onChange={() => setIsAgreed(prev => !prev)} style={staticStyles.hiddenInput} />
                            <img src={isAgreed ? boxCheckedIcon : boxUncheckedIcon} alt="check"
                                style={staticStyles.agreementCheckboxIcon} />
                            <span style={staticStyles.agreementText}>정기결제 상품 구매 동의 및 다음 결제일 확인</span>
                        </label>
                    </>
                )}

                {/* 추가 상품 목록 표시 */}
                <div style={staticStyles.horizonBar} />
                <p style={staticStyles.extraProductsTitle}>추가 상품</p>
                <div style={staticStyles.extraProductsList}>
                    {additionalProducts.length > 0 ? (
                        additionalProducts.map(product => (
                            <ExtraProductItem
                                key={product.id}
                                product={product}
                                isChecked={selectedAdditionalItems[product.id] > 0}
                                onToggle={handleExtraProductToggle}
                                months={currentMonths} // 개월 수 전달
                            />
                        ))
                    ) : (
                        <div style={{ color: '#747474', fontSize: '14px', padding: '10px 0' }}>추가 상품이 없습니다.</div>
                    )}
                </div>
                <div style={staticStyles.horizonBar} />
                <Footer />
            </main>

            <div style={staticStyles.paymentButtonDiv}>
                <button
                    style={{ ...staticStyles.paymentButton, ...((!selectedId || (isSubscriptionTab && !isAgreed)) && staticStyles.paymentButtonDisabled) }}
                    disabled={!selectedId || (isSubscriptionTab && !isAgreed)}
                    onClick={handlePaymentClick}
                >
                    <label style={staticStyles.paymentButtonLabel}>결제하기</label>
                </button>
            </div>

            {/* 결제 확인 모달 */}
            <ConfirmModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                onConfirm={handleModalConfirm}
                title={paymentSummary.title}
                message={paymentSummary.message}
                isLoading={isPaymentProcessing} // 로딩 상태 전달
            />
        </div>
    );
}

const staticStyles = {
    pageContainer: {
        background: '#0D042D',
        width: '100%',
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch'
    },
    loadingWrapper: {
        flex: 1,
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: 'calc(100vh - 80px)',
        backgroundColor: '#0D042D',
        display: 'flex'
    },
    loadingText: { fontFamily: 'Pretendard', fontSize: '18px', color: '#FCFCFA' },
    retryButton: {
        marginTop: '20px',
        padding: '10px 20px',
        backgroundColor: '#8052FF',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer'
    },
    backgroundWrapper: {
        background: '#0D042D',
        width: '100%',
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        margin: '0 auto',
        boxSizing: 'border-box',
        overflowX: 'hidden'
    },
    fixedHeaderWrapper: { position: 'fixed', top: 0, left: 0, width: '100vw', zIndex: 100, backgroundColor: '#0D042D' },
    statusBar: { flexShrink: 0, backgroundColor: 'transparent' },
    mainContentBase: {
        flex: 1,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '19px',
        boxSizing: 'border-box',
        width: '100%'
    },
    horizonBar: {
        width: '100%',
        marginLeft: '-24px',
        marginRight: '-24px',
        height: '8px',
        background: '#1D1A3E',
        flexShrink: 0
    },
    purchaseSelectContainer: {
        width: '100%',
        height: '41px',
        borderBottom: '1px solid #1D1A3E',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '24px',
        boxSizing: 'border-box',
        flexShrink: 0
    },
    purchaseButtonBase: {
        background: 'transparent',
        border: 'none',
        padding: 0,
        margin: 0,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '41px'
    },
    purchaseButtonTextBase: {
        fontFamily: 'Pretendard',
        fontWeight: '700',
        fontSize: '18px',
        lineHeight: '17px',
        verticalAlign: 'middle'
    },
    purchaseButtonTextActive: { color: '#FFFFFF' },
    purchaseButtonTextInactive: { color: '#747474' },
    purchaseButtonIndicatorBase: {
        marginTop: '10px',
        width: '100%',
        height: '3px',
        borderRadius: '1.5px',
        transition: 'background-color 0.3s'
    },
    purchaseButtonIndicatorActive: { background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)' },
    purchaseButtonIndicatorInactive: { background: 'transparent' },
    subscriptionList: { width: '100%', display: 'flex', flexDirection: 'column', gap: '0px', flexShrink: 0 },
    subscriptionCardOuterBase: { width: '100%', height: '90px', borderRadius: '10px', boxSizing: 'border-box' },
    subscriptionCardBaseCompensated: { padding: '1px', background: '#36344D' },
    gradientBorderWrapper: {
        padding: '1px',
        background: 'linear-gradient(90deg, #8052FF 0%, #FFFFFF 50%, #0231FB 100%)',
        display: 'flex',
        boxSizing: 'border-box'
    },
    innerCardContent: {
        width: '100%',
        height: '100%',
        borderRadius: '10px',
        background: '#1B1931',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 24px',
        boxSizing: 'border-box'
    },
    cardTitleBox: { display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 },
    cardTitle: { fontFamily: 'Pretendard', fontWeight: 700, fontSize: '18px', color: '#FCFCFA', lineHeight: '17px' },
    badgeContainerBase: {
        height: '17px',
        borderRadius: '2px',
        paddingRight: '4px',
        paddingLeft: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        marginLeft: '8px'
    },
    badgeText: { fontFamily: 'Pretendard', fontWeight: 500, fontSize: '12px', lineHeight: '17px', color: '#FFFFFF' },
    priceAreaPass: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        justifyContent: 'center',
        flexShrink: 0,
        gap: '6px'
    },
    monthlyEquivalentPrice: {
        fontFamily: 'Pretendard',
        fontWeight: 500,
        fontSize: '14px',
        lineHeight: '17px',
        color: '#9A9A9A',
        textAlign: 'right'
    },
    totalPassPriceBox: { display: 'flex', alignItems: 'baseline', gap: '6px' },
    totalPassPrice: {
        fontFamily: 'Pretendard',
        fontWeight: 700,
        fontSize: '18px',
        lineHeight: '17px',
        color: '#FCFCFA',
        textAlign: 'right'
    },
    priceAreaMonthly: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-end',
        flexShrink: 0
    },
    originalPriceMonthly: {
        fontFamily: 'Pretendard',
        fontWeight: 700,
        fontSize: '18px',
        lineHeight: '17px',
        color: '#FCFCFA'
    },
    extraProductsTitle: {
        width: '100%',
        fontFamily: 'Pretendard',
        fontWeight: 600,
        fontSize: '20px',
        color: '#FFFFFF',
        lineHeight: '17px',
        textAlign: 'left',
        flexShrink: 0
    },
    extraProductsList: { width: '100%', display: 'flex', flexDirection: 'column', gap: '10px', flexShrink: 0 },
    hiddenInput: { position: 'absolute', opacity: 0, width: 0, height: 0 },
    extraProductItemBase: {
        width: '100%',
        height: '50px',
        borderRadius: '6px',
        boxSizing: 'border-box',
        padding: '0',
        display: 'flex',
        alignItems: 'center',
        background: '#1B1931',
        cursor: 'pointer'
    },
    extraProductItem: {
        unchecked: { border: '1px solid #36344D' },
        checked: {
            border: 'none',
            padding: '1px',
            background: 'linear-gradient(90deg, #8052FF 0%, #FFFFFF 50%, #0231FB 100%)',
            borderRadius: '6px'
        }
    },
    extraProductInnerContent: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: '0 15px',
        background: '#1B1931',
        borderRadius: '6px',
        boxSizing: 'border-box'
    },
    extraProductCircleCheckbox: {
        base: {
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            flexShrink: 0,
            marginRight: '10px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            transition: 'all 0.2s ease',
            boxSizing: 'border-box',
            backgroundColor: 'transparent'
        },
        unchecked: { border: '2px solid #36344D', backgroundColor: 'transparent' },
        checked: { border: '2px solid #8052FF', backgroundColor: 'transparent' }
    },
    extraProductInnerDot: { width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#8052FF' },
    extraProductInfoBox: { flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    extraProductTitle: {
        fontFamily: 'Pretendard',
        fontWeight: 500,
        fontSize: '16px',
        lineHeight: '17px',
        color: '#FCFCFA'
    },
    extraProductPrice: {
        fontFamily: 'Pretendard',
        fontWeight: 700,
        fontSize: '16px',
        lineHeight: '17px',
        color: '#FCFCFA',
        textAlign: 'right'
    },
    paymentButtonDivHeight: '100px',
    paymentButtonDiv: {
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 100,
        backgroundColor: '#0D042D',
        padding: '24px 24px calc(16px + env(safe-area-inset-bottom, 0px))',
        boxSizing: 'border-box',
        width: '100%'
    },
    paymentButton: {
        width: '100%',
        height: '50px',
        borderRadius: '6px',
        border: 'none',
        background: 'linear-gradient(90deg, #8052FF 0%, #0231FB 100%)',
        flexShrink: 0,
        padding: '10px 0',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        transition: 'background 0.2s ease'
    },
    paymentButtonDisabled: { background: '#3A394A', cursor: 'not-allowed' },
    paymentButtonLabel: {
        fontFamily: 'Pretendard',
        fontWeight: 700,
        fontSize: '16px',
        lineHeight: '100%',
        color: '#FCFCFA'
    },
    nextBillingText: {
        fontFamily: 'Pretendard',
        fontWeight: 400,
        fontSize: '14px',
        color: '#9A9A9A',
        width: '100%',
        textAlign: 'left'
    },
    agreementContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        cursor: 'pointer',
        width: '100%',
        justifyContent: 'flex-start'
    },
    agreementCheckboxIcon: { width: '20px', height: '20px', flexShrink: 0 },
    agreementText: { fontFamily: 'Pretendard', fontWeight: 400, fontSize: '14px', color: '#FCFCFA', lineHeight: '17px' },
    startDateContainer: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginTop: '10px'
    },
    startDateTitle: {
        fontFamily: 'Pretendard',
        fontWeight: 600,
        fontSize: '16px',
        color: '#FCFCFA'
    },
    startDayGroup: {
        display: 'flex',
        gap: '10px',
        width: '100%'
    },
    startDayButton: {
        flex: 1,
        height: '44px',
        borderRadius: '6px',
        background: '#1B1931',
        border: '1px solid #36344D',
        color: '#747474',
        fontFamily: 'Pretendard',
        fontWeight: 500,
        fontSize: '15px',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    },
    startDayButtonActive: {
        background: 'rgba(128, 82, 255, 0.1)',
        border: '1px solid #8052FF',
        color: '#8052FF'
    }
};

export default Product;